import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

// Default metadata fallback
const DEFAULT_METADATA = {
  title: 'Stay Pair Homes',
  meta_description: 'Flexible mid-term housing — Stay Pair Homes',
  og_title: 'Stay Pair Homes',
  og_description: 'Flexible mid-term housing — Stay Pair Homes',
  og_image_url: '/staypairhomes-logo.png',
  canonical_url: 'https://staypairhomes.com',
  keywords: 'mid-term housing, temporary housing, Los Angeles rentals'
};

/**
 * Hook to fetch and apply page metadata from backend.
 * Automatically updates document title and meta tags.
 */
export const usePageMetadata = (customRoute = null) => {
  const location = useLocation();
  const [metadata, setMetadata] = useState(DEFAULT_METADATA);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchMetadata = async () => {
      const route = customRoute || location.pathname;
      
      try {
        setLoading(true);
        // Remove leading slash for API call
        const cleanRoute = route === '/' ? '' : route.replace(/^\//, '');
        const response = await axios.get(`${API}/api/metadata/${cleanRoute}`);
        const data = response.data;
        
        // Apply metadata
        applyMetadata(data);
        setMetadata(data);
        setError(null);
      } catch (err) {
        console.warn('Failed to fetch metadata, using defaults:', err);
        applyMetadata(DEFAULT_METADATA);
        setMetadata(DEFAULT_METADATA);
        setError(err);
      } finally {
        setLoading(false);
      }
    };

    fetchMetadata();
  }, [location.pathname, customRoute]);

  return { metadata, loading, error };
};

/**
 * Apply metadata to document head.
 */
const applyMetadata = (metadata) => {
  // Update title
  document.title = metadata.title || DEFAULT_METADATA.title;
  
  // Update or create meta tags
  updateMetaTag('description', metadata.meta_description || DEFAULT_METADATA.meta_description);
  updateMetaTag('keywords', metadata.keywords || DEFAULT_METADATA.keywords);
  
  // Open Graph tags
  updateMetaTag('og:title', metadata.og_title || metadata.title || DEFAULT_METADATA.og_title, 'property');
  updateMetaTag('og:description', metadata.og_description || metadata.meta_description || DEFAULT_METADATA.og_description, 'property');
  updateMetaTag('og:image', metadata.og_image_url || DEFAULT_METADATA.og_image_url, 'property');
  updateMetaTag('og:url', metadata.canonical_url || DEFAULT_METADATA.canonical_url, 'property');
  updateMetaTag('og:type', 'website', 'property');
  updateMetaTag('og:site_name', 'Stay Pair Homes', 'property');
  
  // Twitter Card tags
  updateMetaTag('twitter:card', 'summary_large_image', 'name');
  updateMetaTag('twitter:title', metadata.og_title || metadata.title || DEFAULT_METADATA.og_title, 'name');
  updateMetaTag('twitter:description', metadata.og_description || metadata.meta_description || DEFAULT_METADATA.og_description, 'name');
  updateMetaTag('twitter:image', metadata.og_image_url || DEFAULT_METADATA.og_image_url, 'name');
  
  // Canonical URL
  updateCanonicalUrl(metadata.canonical_url || DEFAULT_METADATA.canonical_url);
};

/**
 * Update or create a meta tag.
 */
const updateMetaTag = (name, content, attribute = 'name') => {
  if (!content) return;
  
  let selector = attribute === 'property' 
    ? `meta[property="${name}"]` 
    : `meta[name="${name}"]`;
  
  let element = document.querySelector(selector);
  
  if (!element) {
    element = document.createElement('meta');
    element.setAttribute(attribute, name);
    document.head.appendChild(element);
  }
  
  element.setAttribute('content', content);
};

/**
 * Update canonical URL link tag.
 */
const updateCanonicalUrl = (url) => {
  if (!url) return;
  
  let link = document.querySelector('link[rel="canonical"]');
  
  if (!link) {
    link = document.createElement('link');
    link.setAttribute('rel', 'canonical');
    document.head.appendChild(link);
  }
  
  link.setAttribute('href', url);
};

/**
 * Component wrapper that applies metadata.
 * Use this to wrap page components.
 */
export const PageMetadata = ({ children, route = null }) => {
  usePageMetadata(route);
  return <>{children}</>;
};

export default usePageMetadata;
